//@ts-check

// Maps our internal DataTable column keys (which are often locale-suffixed, e.g. `_en`/`_es_US`)
// to the BFF's `BulkProductFieldValues` shape. `locale`, when set, nests the value under
// `fieldValues[apiField][locale]` instead of `fieldValues[apiField]` directly.
// Fields with no entry here aren't sent to the API yet (e.g. `subCategoryId` - the API only
// accepts the structured `subCategories` array, not a flat id).
// The API treats `null` as "clear this field" - an empty string would be persisted as-is.
const toNullIfEmpty = value => (typeof value === 'string' && value.trim() === '' ? null : value)

const toBoolean = value => String(value).trim().toUpperCase() === 'TRUE'

const toNumber = value => {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

// Dates are displayed/edited as MM/DD/YYYY (see formatDateToMMDDYYYY) - convert back to ISO.
const toISODate = value => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value ?? '').trim())
  if (!match) return value
  const [, month, day, year] = match
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toISOString()
}

const COLUMN_TO_FIELD_MAP = {
  showOnECommerce: {apiField: 'showOnEcommerce', transform: toBoolean},
  sorting: {apiField: 'sorting', transform: toNumber},
  displayName_en: {apiField: 'displayName', locale: 'en'},
  displayName_locale: {apiField: 'displayName', locale: 'localized'},
  size: {apiField: 'displaySize', locale: 'en'},
  displaySize_locale: {apiField: 'displaySize', locale: 'localized'},
  displayCategoryId: {apiField: 'displayCategoryId'},
  displayCategory: {apiField: 'displayCategory', locale: 'en'},
  displayBrandId: {apiField: 'displayBrandId'},
  displayBrand: {apiField: 'displayBrand', locale: 'en'},
  displaySizeId: {apiField: 'displaySizeId', transform: toNumber},
  multipleSellingUnits: {apiField: 'multipleSellingUnits', transform: toNumber},
  minimumOrderQuantity: {apiField: 'minimumOrderQuantity', transform: toNumber},
  keywords_en: {apiField: 'keywords', locale: 'en'},
  newSkuTag: {apiField: 'isNewProduct', transform: toBoolean},
  newSkuStartDate: {apiField: 'skuStartDate', transform: toISODate},
  newSkuEndDate: {apiField: 'skuEndDate', transform: toISODate},
  productDescription_locale: {apiField: 'productDescription', locale: 'localized'},
  productDescription_en: {apiField: 'productDescription', locale: 'en'},
  // subCategoryId/subCategory_en/subCategory_locale are combined into the `subCategories` array - see SUBCATEGORY_COLUMN_MAP.
  sizeFormatId: {apiField: 'sizeId'},
  sizeFormat_en: {apiField: 'sizeByLocale', locale: 'en'},
  productTypeId: {apiField: 'productTypeId'},
  productType_en: {apiField: 'productTypeByLocale', locale: 'en'},
  suggestedProductSellingPrice: {apiField: 'suggestedSellingPrice', transform: toNumber},
  segmentsToShow: {
    apiField: 'segments',
    transform: segments =>
      Array.isArray(segments) ? segments.map(({segmentId, segmentName}) => ({id: segmentId, name: segmentName})) : [],
  },
}

// subCategoryId/subCategory_en/subCategory_locale don't map to flat fields - the API expects a single
// `subCategories` entry of `{code, values}`, with the labels keyed by locale under `values`.
const SUBCATEGORY_COLUMN_MAP = {
  subCategoryId: {key: 'code'},
  subCategory_en: {key: 'values', locale: 'en'},
  subCategory_locale: {key: 'values', locale: 'localized'},
}

const resolveLocale = (mappingLocale, locale) => (mappingLocale === 'localized' ? locale : mappingLocale)

const assignField = (target, key, fieldLocale, value) => {
  target[key] = fieldLocale ? {...target[key], [fieldLocale]: value} : value
}

/**
 * Converts a row's edited table values (keyed by DataTable columnKey) into the
 * `fieldValues` shape expected by the bulk edit API. Only the fields actually edited are sent -
 * the backend persists each value independently and no longer needs a paired id/label resent
 * together.
 * @param {Record<string, any>} editedFields
 * @param {string} [locale] Country-specific locale selected from the languages feature flag.
 */
export const mapEditedValuesToFieldValues = (editedFields, locale = 'es_US') => {
  const fieldValues = {}
  const subCategory = {}

  Object.entries(editedFields ?? {}).forEach(([columnKey, rawValue]) => {
    const value = toNullIfEmpty(rawValue)

    const subCategoryMapping = SUBCATEGORY_COLUMN_MAP[columnKey]
    if (subCategoryMapping) {
      assignField(subCategory, subCategoryMapping.key, resolveLocale(subCategoryMapping.locale, locale), value)
      return
    }

    const mapping = COLUMN_TO_FIELD_MAP[columnKey]
    if (!mapping) return

    const transformedValue = mapping.transform ? mapping.transform(value) : value
    assignField(fieldValues, mapping.apiField, resolveLocale(mapping.locale, locale), transformedValue)
  })

  if (Object.keys(subCategory).length > 0) {
    fieldValues.subCategories = [subCategory]
  }

  return fieldValues
}
